/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// Dashboard.jsx — ported from the source app forecasting.
// Adapted for Brand Command: Supabase-Auth user model (no MSAL), direct-RLS
// service, `canEdit` prop instead of a permission set, and an fc-module root
// so the recolor tokens apply. Layout is otherwise the source verbatim.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  loadForecastData, updateForecastCase, createForecastRow, deleteForecastRow,
  fetchForecastCells, bulkUpsertForecastCells,
} from "../utils/forecastService.js";
import { cellKey, buildSeedWrite, buildSeedOutcome } from "../utils/seedForecast.js";
import ForecastGrid from "./ForecastGrid.jsx";
import ForecastChart from "./ForecastChart.jsx";
import HistoryPanel from "./HistoryPanel.jsx";
import YearMultiSelect from "./YearMultiSelect.jsx";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import ExportOptionsModal from "./ExportOptionsModal.jsx";
import { exportGridToExcel, LOGO_NOTICE } from "../utils/exportForecast.js";
import MobileFilterDrawer from "./MobileFilterDrawer.jsx";
import SeedYearModal from "./SeedYearModal.jsx";
import "./fc-tokens.css";
import "./Dashboard.css";

export default function Dashboard({ user, canEdit = true, onHome }) {
  const canEditForecast = canEdit;

  // Display name for the active Supabase user (full_name → name → email).
  const actorName =
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email ||
    "You";

  // ─── Data from Supabase ────────────────────────────────────────────────────
  const [rawForecast, setRawForecast] = useState([]);
  const [rawActuals, setRawActuals] = useState([]);
  const [states,      setStates]      = useState([]); // [{ id, state_name, region, distributor }]
  const [products,    setProducts]    = useState([]); // [{ id, name, active, sizes: { name } }]

  // ─── UI state ──────────────────────────────────────────────────────────────
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [saving,        setSaving]        = useState(null);
  const [creating,      setCreating]      = useState(null);
  const [saveMsg,       setSaveMsg]       = useState(null);
  const [lastRefresh,   setLastRefresh]   = useState(null);
  const [historyOpen,   setHistoryOpen]   = useState(false);
  const [exportOpen,    setExportOpen]     = useState(false);
  const [seedOpen,      setSeedOpen]       = useState(false);
  const [changeHistory, setChangeHistory] = useState([]);
  const [viewMode,      setViewMode]      = useState("grid");
  const [lockedRows,    setLockedRows]    = useState(() => {
    try {
      const saved = localStorage.getItem("bc_forecast_locked_rows");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  // ─── Selector state ────────────────────────────────────────────────────────
  const [selectedYears,       setSelectedYears]       = useState([]); // string[] — one or many
  const [selectedRegion,      setSelectedRegion]      = useState("");
  const [selectedState,       setSelectedState]       = useState("");
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [selectedSize,        setSelectedSize]        = useState("");
  const [selectedPackSize,    setSelectedPackSize]    = useState("");
  const [selectedTag,         setSelectedTag]         = useState("");
  const [selectedFlavor,      setSelectedFlavor]      = useState("");
  const [selectedSku,         setSelectedSku]         = useState("");

  // ─── Data loading ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { states: s, products: p, rawForecast: f, rawActuals: a } = await loadForecastData();
      setStates(s);
      setProducts(p);
      setRawForecast(f);
      setRawActuals(a ?? []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Load error:", err);
      setError(err.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Selector options ──────────────────────────────────────────────────────

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const fromData = new Set(rawForecast.map(r => r.fields.Year).filter(Boolean));
    const years = new Set([...fromData, String(currentYear), String(currentYear + 1), String(currentYear + 2)]);
    return [...years].sort().map(v => ({ value: v, label: v }));
  }, [rawForecast]);

  const regionOptions = useMemo(() => {
    const set = new Set(states.map(s => s.region).filter(Boolean));
    return [...set].sort().map(v => ({ value: v, label: v }));
  }, [states]);

  const stateOptions = useMemo(() => {
    const filtered = selectedRegion
      ? states.filter(s => s.region === selectedRegion)
      : states;
    const unique = [...new Set(filtered.map(s => s.state_name).filter(Boolean))].sort();
    return unique.map(v => ({ value: v, label: v }));
  }, [states, selectedRegion]);

  const distributorOptions = useMemo(() => {
    const filtered = selectedState
      ? states.filter(s => s.state_name === selectedState)
      : states;
    const unique = [...new Set(filtered.map(s => s.distributor).filter(Boolean))].sort();
    return unique.map(v => ({ value: v, label: v }));
  }, [states, selectedState]);

  // SKU name → size/flavor name maps
  const skuToSizeMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.name] = p.sizes?.name ?? ""; });
    return map;
  }, [products]);

  const skuToFlavorMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.name] = p.flavors?.name ?? ""; });
    return map;
  }, [products]);

  const skuToPackSizeMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.name] = p.pack_sizes?.name ?? ""; });
    return map;
  }, [products]);

  const skuToTagMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.name] = Array.isArray(p.tag) ? p.tag : p.tag ? [p.tag] : []; });
    return map;
  }, [products]);

  // Tags come from the account's own SKUs (System Settings → Product SKUs).
  // These used to be four hardcoded flavor names inlined as <option>s, which
  // offered every tenant tags they had never used and hid the ones they had.
  const tagOptions = useMemo(
    () => [...new Set(Object.values(skuToTagMap).flat())]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b)),
    [skuToTagMap]
  );

  const sizeOptions = useMemo(() => {
    const set = new Set(products.map(p => p.sizes?.name).filter(Boolean));
    return [...set].sort().map(v => ({ value: v, label: v }));
  }, [products]);

  const packSizeOptions = useMemo(() => {
    let prods = products;
    if (selectedSize) prods = prods.filter(p => (p.sizes?.name ?? "") === selectedSize);
    const set = new Set(prods.map(p => p.pack_sizes?.name).filter(Boolean));
    return [...set].sort().map(v => ({ value: v, label: v }));
  }, [products, selectedSize]);

  const flavorOptions = useMemo(() => {
    let prods = products;
    if (selectedSize)     prods = prods.filter(p => (p.sizes?.name ?? "")      === selectedSize);
    if (selectedPackSize) prods = prods.filter(p => (p.pack_sizes?.name ?? "") === selectedPackSize);
    if (selectedTag)      prods = prods.filter(p => (Array.isArray(p.tag) ? p.tag : []).includes(selectedTag));
    const set = new Set(prods.map(p => p.flavors?.name).filter(Boolean));
    return [...set].sort().map(v => ({ value: v, label: v }));
  }, [products, selectedSize, selectedPackSize, selectedTag]);

  const skuOptions = useMemo(() => {
    let names = products.map(p => p.name).filter(Boolean);
    if (selectedSize)     names = names.filter(n => skuToSizeMap[n]     === selectedSize);
    if (selectedPackSize) names = names.filter(n => skuToPackSizeMap[n] === selectedPackSize);
    if (selectedTag)      names = names.filter(n => skuToTagMap[n].includes(selectedTag));
    if (selectedFlavor)   names = names.filter(n => skuToFlavorMap[n]   === selectedFlavor);
    return [...new Set(names)].sort().map(v => ({ value: v, label: v }));
  }, [products, selectedSize, selectedPackSize, selectedTag, selectedFlavor, skuToSizeMap, skuToPackSizeMap, skuToTagMap, skuToFlavorMap]);

  // Full SKU list for grid rows (filtered by size, pack size, tag, and flavor)
  const skuNames = useMemo(() => {
    let names = products.map(p => p.name).filter(Boolean);
    if (selectedSize)     names = names.filter(n => skuToSizeMap[n]     === selectedSize);
    if (selectedPackSize) names = names.filter(n => skuToPackSizeMap[n] === selectedPackSize);
    if (selectedTag)      names = names.filter(n => skuToTagMap[n].includes(selectedTag));
    if (selectedFlavor)   names = names.filter(n => skuToFlavorMap[n]   === selectedFlavor);
    return [...new Set(names)].sort();
  }, [products, selectedSize, selectedPackSize, selectedTag, selectedFlavor, skuToSizeMap, skuToPackSizeMap, skuToTagMap, skuToFlavorMap]);

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    setSelectedState("");
    setSelectedDistributor("");
  };

  const handleStateChange = (state) => {
    setSelectedState(state);
    if (state) {
      const distrs = [...new Set(
        states.filter(s => s.state_name === state).map(s => s.distributor).filter(Boolean)
      )].sort();
      setSelectedDistributor(distrs[0] ?? "");
    } else {
      setSelectedDistributor("");
    }
  };

  const handleSizeChange = (size) => {
    setSelectedSize(size);
    setSelectedPackSize("");
    setSelectedFlavor("");
    setSelectedSku("");
  };

  const handlePackSizeChange = (packSize) => {
    setSelectedPackSize(packSize);
    setSelectedTag("");
    setSelectedFlavor("");
    setSelectedSku("");
  };

  const handleFlavorChange = (flavor) => {
    setSelectedFlavor(flavor);
    setSelectedSku("");
  };

  // ─── Save existing record ──────────────────────────────────────────────────
  const handleSave = useCallback(async (itemId, newValue) => {
    setSaving(itemId);
    setSaveMsg(null);
    const existing = rawForecast.find(r => r.id === itemId);
    const oldValue = existing?.fields?.CaseForecast ?? null;
    try {
      await updateForecastCase(itemId, newValue, actorName);

      setRawForecast(prev => prev.map(row =>
        row.id !== itemId ? row : {
          ...row,
          fields: { ...row.fields, CaseForecast: newValue, Modified: new Date().toISOString() },
        }
      ));

      setChangeHistory(prev => [...prev, {
        type: "edit", timestamp: new Date(),
        sku:   existing?.fields?.ProductSKU || "",
        month: existing?.fields?.Month      || "",
        year:  existing?.fields?.Year       || "",
        state: existing?.fields?.State      || "",
        oldValue, newValue,
        user: actorName,
        undoData: { itemId, oldValue },
      }]);

      setSaveMsg({ type: "success", text: "Saved." });
    } catch (err) {
      console.error("Save error:", err);
      setSaveMsg({ type: "error", text: `Save failed: ${err.message}` });
    } finally {
      setSaving(null);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }, [rawForecast, actorName]);

  // ─── Delete a record (user cleared the cell) ──────────────────────────────
  const handleDelete = useCallback(async (itemId) => {
    const existing = rawForecast.find(r => r.id === itemId);
    const oldValue = existing?.fields?.CaseForecast ?? null;
    try {
      await deleteForecastRow(itemId);
      setRawForecast(prev => prev.filter(r => r.id !== itemId));
      setChangeHistory(prev => [...prev, {
        type: "delete", timestamp: new Date(),
        sku:   existing?.fields?.ProductSKU || "",
        month: existing?.fields?.Month      || "",
        year:  existing?.fields?.Year       || "",
        state: existing?.fields?.State      || "",
        oldValue, newValue: null,
        user: actorName,
        undoData: { itemId, oldValue },
      }]);
    } catch (err) {
      console.error("Delete error:", err);
      setSaveMsg({ type: "error", text: `Delete failed: ${err.message}` });
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }, [rawForecast, actorName]);

  // ─── Create new record ─────────────────────────────────────────────────────
  const handleCreate = useCallback(async (month, skuName, value, year) => {
    const creatingKey = `${skuName}|${month}`;
    const stateRow = selectedDistributor
      ? states.find(s => s.state_name === selectedState && s.distributor === selectedDistributor)
      : states.find(s => s.state_name === selectedState);
    const productRow = products.find(p => p.name === skuName);

    if (!stateRow || !productRow) {
      setSaveMsg({ type: "error", text: "Could not resolve state or product." });
      setTimeout(() => setSaveMsg(null), 4000);
      return;
    }

    setCreating(creatingKey);
    setSaveMsg(null);
    try {
      const result = await createForecastRow({
        stateId:      stateRow.id,
        productId:    productRow.id,
        year:         Number(year),
        month,
        caseForecast: value,
        updatedBy:    actorName,
      });

      const newRow = {
        id: result.id,
        fields: {
          State:             selectedState,
          Region:            stateRow.region      ?? "",
          Distributor:       stateRow.distributor ?? "",
          Year:              String(year),
          Month:             month,
          ProductSKU:        skuName,
          CaseForecast:      value,
          ModifiedByDisplay: actorName,
          Modified:          result.updated_at    ?? new Date().toISOString(),
          _stateId:          stateRow.id,
          _productId:        productRow.id,
          _sizeName:         productRow.sizes?.name ?? "",
        },
      };

      setRawForecast(prev => [...prev, newRow]);
      setChangeHistory(prev => [...prev, {
        type: "create", timestamp: new Date(),
        sku: skuName, month, year, state: selectedState,
        newValue: value,
        user: actorName,
        undoData: { itemId: result.id },
      }]);
      setSaveMsg({ type: "success", text: "Forecast entry created." });
    } catch (err) {
      console.error("Create error:", err);
      setSaveMsg({ type: "error", text: `Create failed: ${err.message}` });
    } finally {
      setCreating(null);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }, [states, products, selectedState, selectedDistributor, actorName]);

  // ─── Paste a copied row into a target SKU ─────────────────────────────────
  // Copy/paste is only exposed in the editable view (exactly one year), so the
  // target year is unambiguous.
  const handlePasteRow = useCallback(async (targetSku, monthValues) => {
    setSaveMsg(null);
    const pasteYear = selectedYears[0];
    const stateRow = selectedDistributor
      ? states.find(s => s.state_name === selectedState && s.distributor === selectedDistributor)
      : states.find(s => s.state_name === selectedState);
    const productRow = products.find(p => p.name === targetSku);
    if (!stateRow || !productRow) {
      setSaveMsg({ type: "error", text: "Could not resolve state or product for paste." });
      setTimeout(() => setSaveMsg(null), 4000);
      return;
    }

    try {
      const saves = Object.entries(monthValues).map(([month, value]) => {
        const existing = rawForecast.find(r =>
          r.fields.State      === selectedState  &&
          String(r.fields.Year) === String(pasteYear) &&
          r.fields.ProductSKU === targetSku       &&
          r.fields.Month      === month
        );
        if (existing) {
          const oldValue = existing.fields.CaseForecast;
          return updateForecastCase(existing.id, value, actorName)
            .then(() => ({ month, value, id: existing.id, isNew: false, oldValue }));
        } else {
          return createForecastRow({
            stateId: stateRow.id, productId: productRow.id,
            year: Number(pasteYear), month, caseForecast: value,
            updatedBy: actorName,
          }).then(result => ({ month, value, id: result.id, isNew: true, oldValue: null }));
        }
      });

      const results = await Promise.all(saves);

      setRawForecast(prev => {
        let updated = [...prev];
        results.filter(Boolean).forEach(({ month, value, id, isNew }) => {
          if (!isNew) {
            updated = updated.map(r => r.id !== id ? r : {
              ...r, fields: { ...r.fields, CaseForecast: value, Modified: new Date().toISOString() },
            });
          } else {
            updated.push({
              id,
              fields: {
                State: selectedState, Month: month, Year: String(pasteYear),
                ProductSKU: targetSku, CaseForecast: value,
                Region: stateRow.region ?? "", Distributor: stateRow.distributor ?? "",
                ModifiedByDisplay: "", Modified: new Date().toISOString(),
                _stateId: stateRow.id, _productId: productRow.id,
              },
            });
          }
        });
        return updated;
      });

      setChangeHistory(prev => [...prev, {
        type: "paste", timestamp: new Date(),
        sku: targetSku, month: "", year: pasteYear, state: selectedState,
        monthCount: Object.keys(monthValues).length,
        user: actorName,
        undoData: results.filter(Boolean).map(r => ({
          month: r.month, itemId: r.id, oldValue: r.oldValue, isNew: r.isNew,
        })),
      }]);
      setSaveMsg({ type: "success", text: `Row pasted to ${targetSku}.` });
    } catch (err) {
      console.error("Paste error:", err);
      setSaveMsg({ type: "error", text: `Paste failed: ${err.message}` });
    } finally {
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }, [rawForecast, states, products, selectedState, selectedDistributor, selectedYears, actorName]);

  // Same aggregate test the grid uses to decide whether a cell can be typed in
  // (`ForecastGrid.jsx:44`). Kept here too because the Seed button lives in the
  // toolbar, outside the grid, and must not offer what the grid forbids.
  const seedIsAggregate = !selectedDistributor && distributorOptions.length > 1;

  // APP-570 — `handleStateChange` auto-selects the state's alphabetically-first
  // distributor, so the grid, the chart and the export all show ONE
  // distributor's numbers while every filter still just says "Arizona". The
  // auto-pick stays (it is what makes the grid immediately editable); this flag
  // only makes it VISIBLE. True only where it is genuinely a narrowing — a
  // state served by more than one distributor.
  const scopeNarrowed =
    !!selectedState && !!selectedDistributor && distributorOptions.length > 1;

  // ─── Seed a whole year from actuals or a previous forecast (backlog 141) ───
  // The plan is decided by utils/seedForecast.planSeed; this only executes it.
  // Writes go through the module's whitelisted service — bulk is not a reason to
  // reach past the service layer.
  //
  // APP-522 — this ran one createForecastRow / updateForecastCase per cell, 25
  // concurrent: 240 round trips to seed a 20-SKU year. It is now three calls
  // total regardless of size — read the (state, year) as the DB holds it, upsert
  // the block on `forecasts_unique_cell`, read the ids back off the same
  // statement.
  //
  // The pre-read is what makes that safe. Per-cell INSERTs got two things for
  // free that an upsert does not: a row that already existed failed loudly
  // instead of being silently updated, and the failure is what told us "we did
  // not create this row, so undo must not delete it". Both are now decided
  // against the pre-read (see seedForecast.buildSeedWrite / buildSeedOutcome),
  // which is a stronger source than the old code used — it took `oldValue` from
  // the in-memory grid, which can be minutes stale.
  const handleSeedYear = useCallback(async (plan) => {
    if (!plan?.ops?.length) return;
    const targetYear = selectedYears[0];

    // Resolve the ONE sales_states row this seed writes against. A bare
    // `.find()` on state name returns whichever row sorts first, which in a
    // two-distributor state files everything under an arbitrary distributor —
    // so refuse instead of guessing. `seedCanRun` keeps the button disabled in
    // that case; this is the belt-and-braces, because the guess is silent and
    // the write is 240 rows deep.
    const stateRows = states.filter(s => s.state_name === selectedState);
    const stateRow = selectedDistributor
      ? stateRows.find(s => s.distributor === selectedDistributor)
      : (stateRows.length === 1 ? stateRows[0] : null);
    if (!stateRow) {
      setSaveMsg({
        type: "error",
        text: stateRows.length > 1
          ? "Pick a distributor — this state has more than one."
          : "Could not resolve the state to seed.",
      });
      setTimeout(() => setSaveMsg(null), 4000);
      return;
    }

    const productById     = new Map(products.map(p => [p.id, p]));
    const productIdBySku  = new Map(products.map(p => [p.name, p.id]));
    const skuByProductId  = new Map(products.map(p => [p.id, p.name]));
    setSaveMsg(null);

    try {
      // 1. What the (state, year) actually holds right now. `strict` inside the
      //    service: a truncated read here would read as "those cells are empty",
      //    which is the one error that makes undo destructive.
      const priorCells = await fetchForecastCells({ stateId: stateRow.id, year: Number(targetYear) });
      const priorByKey = new Map(priorCells.map(c => [cellKey(c.productId, c.month), c]));

      // 2. The cells to send, re-decided against that read.
      const { cells, skippedOccupied, unknownSkus } = buildSeedWrite({
        ops: plan.ops,
        overwrite: !!plan.overwrite,
        productIdBySku,
        priorByKey,
      });
      if (unknownSkus.length) console.error("[seed] unknown SKUs skipped:", [...new Set(unknownSkus)]);

      if (!cells.length) {
        // Every cell was already filled (or already held this exact value) by
        // the time we read the DB — a completed no-op, so close out. The other
        // case is a broken plan, so leave the modal up to be corrected.
        if (skippedOccupied) {
          setSaveMsg({ type: "success", text: `Nothing to seed — all ${skippedOccupied.toLocaleString()} cell${skippedOccupied === 1 ? " already holds" : "s already hold"} that value.` });
          setSeedOpen(false);
        } else {
          setSaveMsg({ type: "error", text: "Nothing to seed — no cell in the plan could be matched to a product." });
        }
        return;
      }

      // 3. One statement (chunked only for very large blocks).
      const { rows, failed: failedCells } = await bulkUpsertForecastCells({
        stateId:   stateRow.id,
        year:      Number(targetYear),
        cells,
        updatedBy: actorName,
      });

      const done = buildSeedOutcome({ rows, priorByKey, skuByProductId });
      const failed = failedCells + unknownSkus.length;

      // Reconcile by id: a cell the pre-read found is `isNew: false` even when
      // the in-memory grid never had it (another tab, a stale load), so match on
      // what we have and append what we don't rather than assuming the two agree.
      setRawForecast(prev => {
        const indexById = new Map(prev.map((r, i) => [r.id, i]));
        const updated = [...prev];
        for (const op of done) {
          const at = indexById.get(op.id);
          const stamp = op.updatedAt ?? new Date().toISOString();
          if (at != null) {
            updated[at] = {
              ...updated[at],
              fields: {
                ...updated[at].fields,
                CaseForecast: op.value, ModifiedByDisplay: actorName, Modified: stamp,
              },
            };
          } else {
            const productRow = productById.get(op.productId);
            updated.push({
              id: op.id,
              fields: {
                State: selectedState, Month: op.month, Year: String(targetYear),
                ProductSKU: op.sku, CaseForecast: op.value,
                Region: stateRow.region ?? "", Distributor: stateRow.distributor ?? "",
                ModifiedByDisplay: actorName,
                Modified: stamp,
                _stateId: stateRow.id, _productId: op.productId,
                _sizeName: productRow?.sizes?.name ?? "",
              },
            });
          }
        }
        return updated;
      });

      // One history entry for the whole run, in the paste entry's undoData shape
      // so handleUndo's existing branch reverses it — a seed that cannot be taken
      // back in one click is worse than typing the cells.
      if (done.length) {
        setChangeHistory(prev => [...prev, {
          type: "seed", timestamp: new Date(),
          sku: `${done.length.toLocaleString()} cells seeded`,
          month: "", year: targetYear, state: selectedState,
          cellCount: done.length,
          user: actorName,
          undoData: done.map(op => ({
            month: op.month, itemId: op.id, oldValue: op.oldValue, isNew: op.isNew,
          })),
        }]);
      }

      const kept = skippedOccupied
        ? ` ${skippedOccupied.toLocaleString()} already filled, left alone.`
        : "";
      setSaveMsg(failed
        ? { type: "error", text: `Seeded ${done.length} cell${done.length === 1 ? "" : "s"}; ${failed} failed.${kept}` }
        : { type: "success", text: `Seeded ${done.length} cell${done.length === 1 ? "" : "s"} into ${targetYear}.${kept}` });
      setSeedOpen(false);
    } catch (err) {
      console.error("Seed error:", err);
      setSaveMsg({ type: "error", text: `Seed failed: ${err.message}` });
    } finally {
      setTimeout(() => setSaveMsg(null), 4000);
    }
  }, [states, products, selectedState, selectedDistributor, selectedYears, actorName]);

  // ─── Undo a history entry ─────────────────────────────────────────────────
  const handleUndo = useCallback(async (entry) => {
    if (!entry?.undoData) return;
    setSaveMsg(null);
    try {
      const { type, undoData } = entry;

      if (type === "edit") {
        const { itemId, oldValue } = undoData;
        await updateForecastCase(itemId, oldValue, actorName);
        setRawForecast(prev => prev.map(r =>
          r.id !== itemId ? r : { ...r, fields: { ...r.fields, CaseForecast: oldValue } }
        ));
      }

      if (type === "create") {
        await deleteForecastRow(undoData.itemId);
        setRawForecast(prev => prev.filter(r => r.id !== undoData.itemId));
      }

      // "seed" writes the same undoData shape as "paste" (backlog 141), so the
      // two reverse identically — one branch, not two that can drift.
      //
      // APP-554/555 — this used to be a bare serial `for … await`. On a 240-cell
      // seed, a throw at row 100 aborted the loop, so the DB was half-reverted
      // while the state update and the history removal below both never ran:
      // the grid still showed the whole seed and STILL OFFERED UNDO, which on a
      // second press re-issued deletes for rows that were already gone. The
      // operation that puts the numbers back had none of the safety the write
      // was built with. It now mirrors `handleSeedYear` exactly — batched,
      // `allSettled`, and it records what actually reverted.
      if (type === "paste" || type === "seed") {
        const BATCH = 25;
        const reverted = [];
        let failed = 0;
        for (let i = 0; i < undoData.length; i += BATCH) {
          const settled = await Promise.allSettled(undoData.slice(i, i + BATCH).map(async (item) => {
            if (item.isNew) await deleteForecastRow(item.itemId);
            else await updateForecastCase(item.itemId, item.oldValue, actorName);
            return item;
          }));
          settled.forEach((r) => {
            if (r.status === "fulfilled") reverted.push(r.value);
            else { failed++; console.error("[undo]", r.reason); }
          });
        }

        // Move only the rows that really reverted. A half-undo must leave the
        // grid showing what the DB holds, not what we hoped it would hold.
        setRawForecast(prev => {
          let updated = [...prev];
          for (const item of reverted) {
            if (item.isNew) {
              updated = updated.filter(r => r.id !== item.itemId);
            } else {
              updated = updated.map(r => r.id !== item.itemId ? r : {
                ...r, fields: { ...r.fields, CaseForecast: item.oldValue },
              });
            }
          }
          return updated;
        });

        if (failed) {
          // Keep the entry, minus what already reverted, so a retry does not
          // re-delete rows that are gone — the exact loop the old code created.
          const remaining = undoData.filter(d => !reverted.includes(d));
          setChangeHistory(prev => prev.map(e => e !== entry ? e : {
            ...e,
            undoData: remaining,
            cellCount: remaining.length,
            sku: `${remaining.length.toLocaleString()} cells left to undo`,
          }));
          setSaveMsg({ type: "error", text: `Undid ${reverted.length}; ${failed} failed. Press Undo again to retry the rest.` });
          return;
        }
      }

      setChangeHistory(prev => prev.filter(e => e !== entry));
      setSaveMsg({ type: "success", text: "Change undone." });
    } catch (err) {
      console.error("Undo error:", err);
      setSaveMsg({ type: "error", text: `Undo failed: ${err.message}` });
    } finally {
      setTimeout(() => setSaveMsg(null), 3000);
    }
  }, [actorName]);

  // ─── Lock / unlock a row ───────────────────────────────────────────────────
  const handleToggleLock = useCallback((sku) => {
    const key = `${selectedState}|${sku}`;
    setLockedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      localStorage.setItem("bc_forecast_locked_rows", JSON.stringify([...next]));
      return next;
    });
  }, [selectedState]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fc-module dashboard">

      {/* ── Toolbar ── */}
      <div className="dashboard-toolbar">
        <div className="toolbar-left">
          <HomeButton onClick={onHome} />
          <h2 className="toolbar-title">Sales Forecast</h2>
          {lastRefresh && (
            <span className="toolbar-refresh-time">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="toolbar-right">
          <button
            className="toolbar-btn undo-btn"
            onClick={() => handleUndo(changeHistory[changeHistory.length - 1])}
            disabled={loading || changeHistory.length === 0 || !canEditForecast}
            title={!canEditForecast ? "You don't have permission to edit forecasts" : "Undo last change"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
            </svg>
            Undo
          </button>

          <button
            className="toolbar-btn history-btn"
            onClick={() => setHistoryOpen(true)}
            disabled={loading}
            title="View change history"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            History
            {changeHistory.length > 0 && (
              <span className="history-badge">{changeHistory.length}</span>
            )}
          </button>

          {/* backlog 141 — a blank year is 12 x SKUs x states of typing. Gated on
              EXACTLY the conditions that make a cell editable in the grid
              (`ForecastGrid.jsx:44` — single year, a state, and an unambiguous
              distributor), because those are what resolve a cell to one row.
              APP-552/553: it used to omit the distributor half and wrote into
              the aggregate view the grid deliberately makes read-only. */}
          <button
            className="toolbar-btn seed-btn"
            onClick={() => setSeedOpen(true)}
            disabled={loading || !canEditForecast || selectedYears.length !== 1 || !selectedState || seedIsAggregate}
            title={
              !canEditForecast ? "You don't have permission to edit forecasts"
                : selectedYears.length !== 1 ? "Select exactly one Year to seed"
                : !selectedState ? "Select a State to seed"
                : seedIsAggregate ? "Select a Distributor — this state has more than one, so a month maps to more than one forecast row"
                : `Fill ${selectedYears[0]} from actuals or a previous forecast`
            }
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h4l3 8 4-16 3 8h4"/>
            </svg>
            Seed year
          </button>

          <button
            className="toolbar-btn export-btn"
            onClick={() => setExportOpen(true)}
            disabled={loading || selectedYears.length === 0}
            title={selectedYears.length === 0 ? "Select a Year to export" : "Export the on-screen forecast to a branded spreadsheet"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export
          </button>
          <button className="toolbar-btn refresh-btn" onClick={loadData} disabled={loading}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={loading ? "spin-icon" : ""}>
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* ── Toast ── */}
      {saveMsg && (
        <div className={`toast toast-${saveMsg.type}`}>
          {saveMsg.type === "success"
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          }
          {saveMsg.text}
        </div>
      )}

      {/* ── Selectors — wrapped in MobileFilterDrawer for mobile collapse ── */}
      <MobileFilterDrawer
        summary={[
          selectedYears.join(", "),
          selectedRegion,
          selectedState,
          selectedDistributor,
          selectedSize,
          selectedPackSize,
        ].filter(Boolean).join(" · ")}
        activeCount={
          [selectedYears.length > 0, selectedRegion, selectedState, selectedDistributor, selectedSize, selectedPackSize].filter(Boolean).length
        }
        onReset={() => {
          setSelectedYears([]);
          setSelectedRegion("");
          setSelectedState("");
          setSelectedDistributor("");
          setSelectedSize("");
          setSelectedPackSize("");
        }}
      >
      <div className="selector-bar">
        <div className="selector-group">
          <label className="selector-label">Year</label>
          <YearMultiSelect
            options={yearOptions}
            value={selectedYears}
            onChange={setSelectedYears}
            disabled={loading}
          />
        </div>

        <div className="selector-group">
          <label className="selector-label">Region</label>
          <select
            className="selector-select"
            value={selectedRegion}
            onChange={e => handleRegionChange(e.target.value)}
            disabled={loading}
          >
            <option value="">— All —</option>
            {regionOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label className="selector-label">State</label>
          <select
            className="selector-select"
            value={selectedState}
            onChange={e => handleStateChange(e.target.value)}
            disabled={loading}
          >
            <option value="">— Select —</option>
            {stateOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label className="selector-label">Distributor</label>
          <select
            className="selector-select"
            value={selectedDistributor}
            onChange={e => setSelectedDistributor(e.target.value)}
            disabled={loading}
          >
            <option value="">— All —</option>
            {distributorOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label className="selector-label">Bottle Size</label>
          <select
            className="selector-select"
            value={selectedSize}
            onChange={e => handleSizeChange(e.target.value)}
            disabled={loading}
          >
            <option value="">— All Bottle Sizes —</option>
            {sizeOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label className="selector-label">Pack Size</label>
          <select
            className="selector-select"
            value={selectedPackSize}
            onChange={e => handlePackSizeChange(e.target.value)}
            disabled={loading}
          >
            <option value="">— All Pack Sizes —</option>
            {packSizeOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label className="selector-label">TAG</label>
          <select
            className="selector-select"
            value={selectedTag}
            onChange={e => { setSelectedTag(e.target.value); setSelectedFlavor(""); setSelectedSku(""); }}
            disabled={loading}
          >
            <option value="">— All TAGs —</option>
            {tagOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="selector-group">
          <label className="selector-label">Flavor</label>
          <select
            className="selector-select"
            value={selectedFlavor}
            onChange={e => handleFlavorChange(e.target.value)}
            disabled={loading}
          >
            <option value="">— All Flavors —</option>
            {flavorOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label className="selector-label">Product SKU</label>
          <select
            className="selector-select selector-select-wide"
            value={selectedSku}
            onChange={e => setSelectedSku(e.target.value)}
            disabled={loading}
          >
            <option value="">— All SKUs —</option>
            {skuOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {(selectedYears.length > 0 || selectedRegion || selectedState || selectedDistributor || selectedSize || selectedPackSize || selectedTag || selectedFlavor || selectedSku) && (
          <button
            className="selector-clear"
            onClick={() => {
              setSelectedYears([]);
              setSelectedRegion("");
              setSelectedState("");
              setSelectedDistributor("");
              setSelectedSize("");
              setSelectedPackSize("");
              setSelectedTag("");
              setSelectedFlavor("");
              setSelectedSku("");
            }}
          >
            Clear
          </button>
        )}

        {/* ── View toggle ── */}
        <div className="view-toggle">
          <button
            className={`view-toggle-btn${viewMode === "grid" ? " view-toggle-btn-active" : ""}`}
            onClick={() => setViewMode("grid")}
            title="Grid view"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
            Grid
          </button>
          <button
            className={`view-toggle-btn${viewMode === "chart" ? " view-toggle-btn-active" : ""}`}
            onClick={() => setViewMode("chart")}
            title="Chart view"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 20 9 4 6 12 2 12"/>
            </svg>
            Chart
          </button>
        </div>
      </div>
      </MobileFilterDrawer>

      {/* ── Scope note (APP-570) ──
          Sits OUTSIDE the filter drawer so it survives the mobile collapse: the
          whole point is that the narrowing is visible without opening filters. */}
      {scopeNarrowed && (
        <div className="scope-note" role="status">
          <span className="scope-chip">{selectedState} · {selectedDistributor}</span>
          <span className="scope-note-text">
            {distributorOptions.length} distributors serve {selectedState} — the grid,
            chart and export show {selectedDistributor} only. Set Distributor to
            “— All —” for the whole state.
          </span>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="dashboard-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Loading forecast data…</div>
            <div className="loading-sub">Connecting to database</div>
          </div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <div className="error-title">Failed to load data</div>
            <div className="error-msg">{error}</div>
            <button className="error-retry" onClick={loadData}>Retry</button>
          </div>
        ) : viewMode === "chart" ? (
          <ForecastChart
            selectedState={selectedState}
            selectedRegion={selectedRegion}
            selectedDistributor={selectedDistributor}
            selectedYears={selectedYears}
            selectedSku={selectedSku}
            rawForecast={rawForecast}
            rawActuals={rawActuals}
            skuNames={skuNames}
            skuToSize={skuToSizeMap}
            skuToPackSize={skuToPackSizeMap}
            skuToFlavor={skuToFlavorMap}
            skuToTag={skuToTagMap}
          />
        ) : (
          <ForecastGrid
            selectedState={selectedState}
            selectedRegion={selectedRegion}
            selectedDistributor={selectedDistributor}
            distributorOptions={distributorOptions}
            selectedYears={selectedYears}
            selectedSku={selectedSku}
            rawForecast={rawForecast}
            skuNames={skuNames}
            skuToSize={skuToSizeMap}
            skuToPackSize={skuToPackSizeMap}
            onSave={handleSave}
            onDelete={handleDelete}
            onCreate={handleCreate}
            onPasteRow={handlePasteRow}
            onToggleLock={handleToggleLock}
            lockedRows={lockedRows}
            saving={saving}
            creating={creating}
            canEdit={canEditForecast}
          />
        )}
      </div>

      {historyOpen && (
        <HistoryPanel
          entries={changeHistory}
          onClose={() => setHistoryOpen(false)}
          onClear={() => setChangeHistory([])}
          onUndo={async (e) => { await handleUndo(e); setHistoryOpen(false); }}
        />
      )}

      <SeedYearModal
        open={seedOpen}
        onClose={() => setSeedOpen(false)}
        onConfirm={handleSeedYear}
        targetYear={selectedYears[0]}
        hasActuals={rawActuals.length > 0}
        rawForecast={rawForecast}
        rawActuals={rawActuals}
        state={selectedState}
        distributor={selectedDistributor}
        distributorCount={distributorOptions.length}
        skus={selectedSku ? [selectedSku] : skuNames}
      />

      <ExportOptionsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        onConfirm={async ({ includeChart, chartType, chartGroupBy }) => {
          const res = await exportGridToExcel({
            rawForecast, products, skuNames,
            selectedYears, selectedState, selectedRegion, selectedDistributor,
            selectedSize, selectedPackSize, selectedTag, selectedFlavor, selectedSku,
            actorName, includeChart, chartType, chartGroupBy,
          });
          // Unbranded export → hold the dialog open on a one-line warning
          // instead of closing silently (backlog 330).
          return LOGO_NOTICE[res?.logo] || null;
        }}
      />
    </div>
  );
}
